// =============================================================================
// ArchiveModal — the "Past units" surface. Searchable across every entity
// thread that's been archived (sold/scrapped/closed). Tenant-scoped via RLS.
// Restore-to-active in one click; opens the thread to the caller after.
// =============================================================================
import React, { useState } from "react";
import { useArchivedThreads } from "../hooks.js";
import { restoreThread } from "../send.js";

function whenLabel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function ArchiveModal({ tenantId, onClose, onOpen }) {
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const { data, loading } = useArchivedThreads({ tenantId, search: query });

  async function handleRestore(et) {
    setBusyId(et.id);
    try {
      await restoreThread({ tenantId, entityKind: et.entity_kind, entityId: et.entity_id });
      onOpen?.({ ...et, status: "active" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("restoreThread failed:", err?.message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="chat-archive-modal open" onClick={onClose}>
      <div className="chat-archive-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="chat-archive-close" onClick={onClose} aria-label="Close">×</button>
        <div className="chat-archive-h1">Past <em>units</em>.</div>
        <div className="chat-archive-sub">
          Every thread from every unit that's left your floor — preserved with all messages, photos, and voice notes. Search across years. Restore anything that comes back through.
        </div>
        <div className="chat-archive-search-wrap">
          <svg className="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="chat-archive-search"
            placeholder="Search archived threads — VIN, stock #, customer, keyword…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        {loading && <div style={{ padding: 22, color: "var(--text-dim, #6A6D74)" }}>Loading…</div>}
        {!loading && data.length === 0 && (
          <div style={{ padding: 22, color: "var(--text-muted, #4A4D54)" }}>
            {query ? "Nothing matched." : "No archived units yet — every unit that's left the floor will land here."}
          </div>
        )}
        {!loading && data.map((et) => (
          <div key={et.id} className="chat-archive-row">
            <div className="chat-archive-uicon">
              <svg viewBox="0 0 40 20" fill="none" stroke="currentColor" strokeWidth="1.5" width="18" height="14">
                <path d="M 4 16 L 8 9 L 14 7 L 30 7 L 34 9 L 36 14 L 38 16 L 6 16 Z" fill="currentColor" opacity="0.18" />
                <circle cx="12" cy="16" r="2" /><circle cx="30" cy="16" r="2" />
              </svg>
            </div>
            <div>
              <div className="chat-archive-name">{et.entity_label || et.entity_id}</div>
              <div className="chat-archive-sub2">
                {(et.entity_kind || "").replace("_", " ")}
                {et.archived_reason ? ` · ${et.archived_reason}` : ""}
              </div>
            </div>
            <div className="chat-archive-when">{whenLabel(et.archived_at)}</div>
            <button
              type="button"
              className="chat-archive-restore"
              disabled={busyId === et.id}
              onClick={() => handleRestore(et)}
            >
              {busyId === et.id ? "Restoring…" : "Restore"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
